// Client-side verification of a claim's VerificationBundle
// (GET /claims/{claim_id}/bundle). Every signature is recomputed in the
// browser; nothing the server says about validity is trusted except what
// cannot be computed from the bundle (delegation revocation status), which is
// labelled as server-reported.
import type { components } from "../api/schema";
import { verifyEddsaJcs2022, verifyPlatformEcdsaJcs2019 } from "./dataIntegrity";
import { parseCompact, payloadJson, verifyRaw } from "./jws";
import { verifyInclusion } from "./merkle";
import { Resolver } from "./resolve";

type Bundle = components["schemas"]["VerificationBundle"];
type SignedObject = components["schemas"]["SignedObject"];

export interface Check { name: string; ok: boolean; detail?: string }
export interface ObjectResult { kind: string; entryHash?: string; signer?: string; ok: boolean; checks: Check[] }
export interface BundleResult { ok: boolean; objects: ObjectResult[]; didErrors: Record<string, string> }

const ACTOR: Record<string, string> = { claim: "holder_did", vouch_request: "holder_did", vouch: "attester_did",
  anchor: "issuer_did", withdrawal: "attester_did" };

const run = (checks: Check[], name: string, fn: () => true | string | void) => {
  try {
    const r = fn();
    checks.push({ name, ok: r === undefined || r === true, detail: typeof r === "string" ? r : undefined });
  } catch (e) { checks.push({ name, ok: false, detail: (e as Error).message }); }
};

/** Verify a delegation credential (AuthorisedAttester) as it stood at `at`. */
export function verifyDelegation(cred: any, r: Resolver, opts: { issuer: string; attester: string;
  claimType?: string; at: string }): Check[] {
  const checks: Check[] = [];
  const issuer = typeof cred?.issuer === "string" ? cred.issuer : cred?.issuer?.id;
  run(checks, "delegation is an AuthorisedAttester credential",
    () => Array.isArray(cred?.type) && cred.type.includes("AuthorisedAttester") || "not an AuthorisedAttester credential");
  run(checks, "delegation is issued by the issuer the vouch speaks for",
    () => issuer === opts.issuer || `issued by ${issuer}, not ${opts.issuer}`);
  run(checks, "delegation is held by the attester who signed",
    () => cred?.credentialSubject?.id === opts.attester || `held by ${cred?.credentialSubject?.id}`);
  run(checks, "delegation scope covers the claim type", () => !opts.claimType ||
    (cred?.credentialSubject?.scope ?? []).includes(opts.claimType) || `scope ${JSON.stringify(cred?.credentialSubject?.scope)} excludes ${opts.claimType}`);
  run(checks, "delegation was valid when the vouch was signed", () => {
    const t = Date.parse(opts.at);
    if (cred?.validFrom && t < Date.parse(cred.validFrom)) return `not valid until ${cred.validFrom}`;
    if (cred?.validUntil && t > Date.parse(cred.validUntil)) return `expired ${cred.validUntil}`;
    return true;
  });
  run(checks, "issuer's signature on the delegation", () => {
    const proof = Array.isArray(cred.proof) ? cred.proof[0] : cred.proof;
    const kid = String(proof?.verificationMethod ?? "");
    if (kid.split("#")[0] !== issuer) return `signed by ${kid}, not the issuer`;
    const pk = r.key(kid, proof?.created ?? cred.validFrom);
    if (proof?.cryptosuite === "ecdsa-jcs-2019") return verifyPlatformEcdsaJcs2019(cred, pk) || "does not verify";
    if (proof?.cryptosuite === "eddsa-jcs-2022") {
      const { proof: _p, ...unsecured } = cred;
      return verifyEddsaJcs2022(unsecured, proof, pk) || "does not verify";
    }
    return `proof ${proof?.type}/${proof?.cryptosuite} cannot be checked in the browser`;
  });
  return checks;
}

export function verifyBundle(bundle: Bundle): BundleResult {
  const resolver = new Resolver(bundle.dids as any, bundle.compromise ?? []);
  const claimObj = bundle.objects.find((o) => o.kind === "claim");
  const claimEnv = claimObj?.jws ? payloadJson(parseCompact(claimObj.jws)) : null;
  const claimType: string | undefined = claimEnv?.entity?.claim_type;
  const delegations = new Map<string, any>();
  const statuses = new Map<string, any>();
  for (const o of bundle.objects) if (o.kind === "delegation" && o.credential) {
    delegations.set(String((o.credential as any).id), o.credential);
    if (o.status) statuses.set(String((o.credential as any).id), o.status);
  }

  const objects = bundle.objects.filter((o) => o.kind !== "delegation").map((o: SignedObject): ObjectResult => {
    const checks: Check[] = [];
    let env: any = null, signer: string | undefined;
    run(checks, "signature by the key its kid names, at signing time", () => {
      if (!o.jws) return "no JWS";
      const p = parseCompact(o.jws);
      env = payloadJson(p);
      const kid = String(p.header.kid ?? "");
      signer = kid.split("#")[0];
      const pk = resolver.key(kid, env.client_ts);
      return verifyRaw(String(p.header.alg), pk, p.signingInput, p.signature) || "signature does not verify";
    });
    if (env) {
      run(checks, "the signer is the actor the entry names", () => {
        const field = ACTOR[o.kind];
        const named = field ? env.entity?.[field] : undefined;
        return !named || named === signer || `entity names ${named}`;
      });
      run(checks, "the entry is the kind it is filed as", () => env.kind === o.kind || `payload kind ${env.kind}`);
      if (o.kind === "vouch") {
        run(checks, "the vouch is about this claim", () =>
          env.entity?.credentialSubject?.id === `urn:qsv:claim:${bundle.claim_id}` || `subject ${env.entity?.credentialSubject?.id}`);
        const obo = env.entity?.credentialSubject?.on_behalf_of;
        if (obo) {
          const cred = obo.delegation ?? delegations.get(String(obo.delegation_id));
          if (!cred) checks.push({ name: "delegation present in the bundle", ok: false, detail: String(obo.delegation_id) });
          else checks.push(...verifyDelegation(cred, resolver, { issuer: obo.issuer, attester: signer!,
            claimType, at: env.client_ts }));
          const st = cred ? statuses.get(String(cred.id)) : undefined;
          checks.push(st
            ? { name: "delegation not revoked (the issuer's record)", ok: st.status === "active",
                detail: `${st.status}, from the issuer's record${st.attested_by ? ` (attested by ${st.attested_by})` : ""}; not recomputable in the browser` }
            : { name: "delegation not revoked (the issuer's record)", ok: false, detail: "the bundle carries no status for this delegation" });
        }
      }
    }
    if (o.inclusion && (o.inclusion as any).status === "published")
      run(checks, "Polygon inclusion proof recomputes to the published root",
        () => verifyInclusion(o.inclusion as any) || "root does not recompute");
    return { kind: o.kind, entryHash: o.entry_hash, signer, ok: checks.every((c) => c.ok), checks };
  });
  return { ok: objects.length > 0 && objects.every((x) => x.ok) && !Object.keys(resolver.errors).length,
           objects, didErrors: resolver.errors };
}
